import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Star, X } from "lucide-react";
import { submitReview } from "@/lib/api";
import { recaptchaHeaders } from "@/lib/recaptcha";
import { useAuth } from "@/lib/auth";
import { track } from "@/lib/analytics";
import { toast } from "sonner";

const MOST_HELPFUL = [
    { id: "understanding_inspection", label: "Understanding the inspection" },
    { id: "repair_exposure_estimates", label: "Repair exposure estimates" },
    { id: "negotiation_guidance", label: "Negotiation guidance" },
    { id: "report_organization", label: "Report organization" },
    { id: "sharing_options", label: "Sharing / download" },
    { id: "quote_request_flow", label: "Quote request flow" },
    { id: "plain_english", label: "Plain-English explanations" },
    { id: "other", label: "Other" },
];

const PERMISSION_TYPES = [
    { id: "first_name", label: "Yes, with my first name only" },
    { id: "company", label: "Yes, with my company name" },
    { id: "contact_first", label: "Contact me first" },
    { id: "no", label: "No" },
];

/**
 * ReviewModal — unified verified-purchase OR general-feedback form.
 *
 * Props:
 *   open / onClose      — modal control
 *   reportId            — present when verified review; null for general
 *   reportTier          — informational
 *   eligibility         — eligibility payload from /feedback/eligibility (optional)
 *   sourceLocation      — string indicating where the modal was opened from
 *                          ("footer", "dashboard", "report_view", etc.)
 *   defaultGeneral      — force general_feedback even if reportId present
 */
export default function ReviewModal({
    open,
    onClose,
    reportId,
    reportTier,
    eligibility,
    sourceLocation = "report_view",
    defaultGeneral = false,
}) {
    const { user } = useAuth();
    const isVerifiedCandidate = !defaultGeneral && eligibility?.eligible === true;
    const [rating, setRating] = useState(0);
    const [feedbackText, setFeedbackText] = useState("");
    const [mostHelpful, setMostHelpful] = useState([]);
    const [improvement, setImprovement] = useState("");
    const [mayContact, setMayContact] = useState(false);
    const [permission, setPermission] = useState("no");
    const [name, setName] = useState(user?.name || "");
    const [email, setEmail] = useState(user?.email || "");
    const [companyName, setCompanyName] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const toggleHelpful = (id) => {
        setMostHelpful((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
        );
    };

    const onSubmit = async () => {
        const trimmed = feedbackText.trim();
        if (trimmed.length < 2) {
            toast.error("Add a short note.");
            return;
        }
        if (isVerifiedCandidate && !rating) {
            toast.error("Pick a star rating.");
            return;
        }
        setSubmitting(true);
        try {
            const reviewType = isVerifiedCandidate
                ? "verified_purchase"
                : "general_feedback";
            const payload = {
                review_type: reviewType,
                report_id: isVerifiedCandidate ? reportId : null,
                feedback_text: trimmed,
                rating: rating || null,
                most_helpful: mostHelpful,
                improvement_text: improvement.trim() || null,
                may_contact: mayContact,
                testimonial_permission: permission !== "no",
                testimonial_permission_type: permission,
                name: name || null,
                email: email || null,
                public_display_name: name || null,
                company_name: permission === "company" ? companyName || null : null,
                source_location: sourceLocation,
            };
            let token = null;
            if (!user) {
                // Anonymous → grab a reCAPTCHA token for review_submit.
                const h = await recaptchaHeaders("review_submit");
                token = h?.["X-Recaptcha-Token"] || h?.["x-recaptcha-token"];
            }
            const r = await submitReview(payload, token);
            track(
                reviewType === "verified_purchase"
                    ? "verified_review_submitted"
                    : "general_feedback_submitted",
                {
                    rating: rating || null,
                    has_testimonial_permission: permission !== "no",
                    source_location: sourceLocation,
                },
            );
            if ((rating || 0) <= 2 && rating !== 0) {
                track("low_rating_review_submitted", { rating });
            }
            if (permission !== "no") {
                track("testimonial_permission_given", { type: permission });
            }
            setDone(true);
            toast.success("Thanks — your feedback was received.");
        } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(
                typeof detail === "string"
                    ? detail
                    : detail?.message || "Could not submit. Try again shortly.",
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Sheet open={open} onOpenChange={(v) => !v && onClose?.()}>
            <SheetContent
                side="right"
                className="w-full sm:max-w-xl overflow-y-auto bg-bl-surface"
                data-testid="review-modal"
            >
                <SheetHeader>
                    <SheetTitle className="font-display text-bl text-2xl">
                        {isVerifiedCandidate
                            ? "How was your Buyer's Leverage report?"
                            : "Share feedback"}
                    </SheetTitle>
                    <SheetDescription className="text-bl-muted">
                        {isVerifiedCandidate
                            ? "Your feedback helps improve report accuracy, repair exposure estimates, and negotiation guidance."
                            : "Optional — share anything you'd like the team to know. Reviews stay private to our team unless you explicitly opt in to a testimonial."}
                    </SheetDescription>
                </SheetHeader>

                {done ? (
                    <div className="mt-8 text-center" data-testid="review-modal-done">
                        <h3 className="font-display font-semibold text-bl text-xl">
                            Thanks — your feedback was received.
                        </h3>
                        <p className="mt-2 text-sm text-bl-muted">
                            We review every note. We won't follow up unless you
                            asked us to.
                        </p>
                        <button
                            onClick={() => {
                                setDone(false);
                                onClose?.();
                            }}
                            className="bl-btn-primary text-sm mt-5"
                            data-testid="review-modal-close-btn"
                        >
                            Close
                        </button>
                    </div>
                ) : (
                    <div className="mt-6 space-y-5">
                        {isVerifiedCandidate && (
                            <div>
                                <label className="block text-[10px] tracking-[0.18em] uppercase text-bl-muted font-medium mb-2">
                                    Rating
                                </label>
                                <div className="flex gap-1" data-testid="review-modal-rating">
                                    {[1, 2, 3, 4, 5].map((n) => (
                                        <button
                                            key={n}
                                            type="button"
                                            onClick={() => setRating(n)}
                                            aria-label={`${n} star${n === 1 ? "" : "s"}`}
                                            className="p-1 transition-transform hover:scale-110"
                                            data-testid={`review-modal-star-${n}`}
                                        >
                                            <Star
                                                className={
                                                    "w-7 h-7 " +
                                                    (n <= rating
                                                        ? "fill-bl-primary text-bl-primary"
                                                        : "text-bl-muted")
                                                }
                                            />
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div>
                            <label className="block text-[10px] tracking-[0.18em] uppercase text-bl-muted font-medium mb-2">
                                {isVerifiedCandidate
                                    ? "What did you think?"
                                    : "Your feedback"}
                            </label>
                            <textarea
                                value={feedbackText}
                                onChange={(e) => setFeedbackText(e.target.value)}
                                rows={4}
                                placeholder={
                                    isVerifiedCandidate
                                        ? "What worked? What didn't?"
                                        : "Tell us what's on your mind."
                                }
                                className="w-full px-3 py-2 rounded-md border border-bl bg-bl-surface text-bl text-sm resize-y"
                                maxLength={4000}
                                data-testid="review-modal-feedback-text"
                            />
                        </div>

                        {isVerifiedCandidate && (
                            <div>
                                <label className="block text-[10px] tracking-[0.18em] uppercase text-bl-muted font-medium mb-2">
                                    What was most helpful? <span className="text-bl-muted normal-case tracking-normal">(optional)</span>
                                </label>
                                <div className="flex flex-wrap gap-2" data-testid="review-modal-most-helpful">
                                    {MOST_HELPFUL.map((h) => (
                                        <button
                                            key={h.id}
                                            type="button"
                                            onClick={() => toggleHelpful(h.id)}
                                            className={
                                                "px-3 py-1.5 rounded-full text-xs " +
                                                (mostHelpful.includes(h.id)
                                                    ? "bg-bl-primary text-bl-surface"
                                                    : "bg-bl-teal-tint/40 text-bl-primary hover:bg-bl-teal-tint")
                                            }
                                            data-testid={`review-modal-helpful-${h.id}`}
                                        >
                                            {h.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {isVerifiedCandidate && (
                            <div>
                                <label className="block text-[10px] tracking-[0.18em] uppercase text-bl-muted font-medium mb-2">
                                    What could be improved? <span className="text-bl-muted normal-case tracking-normal">(optional)</span>
                                </label>
                                <textarea
                                    value={improvement}
                                    onChange={(e) => setImprovement(e.target.value)}
                                    rows={3}
                                    className="w-full px-3 py-2 rounded-md border border-bl bg-bl-surface text-bl text-sm resize-y"
                                    maxLength={4000}
                                    data-testid="review-modal-improvement"
                                />
                            </div>
                        )}

                        {!user && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <input
                                    type="text"
                                    placeholder="Name (optional)"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="px-3 py-2 rounded-md border border-bl bg-bl-surface text-bl text-sm"
                                    maxLength={160}
                                />
                                <input
                                    type="email"
                                    placeholder="Email (optional)"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="px-3 py-2 rounded-md border border-bl bg-bl-surface text-bl text-sm"
                                    maxLength={200}
                                />
                            </div>
                        )}

                        <label className="flex items-start gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={mayContact}
                                onChange={(e) => setMayContact(e.target.checked)}
                                className="mt-1"
                                data-testid="review-modal-may-contact"
                            />
                            <span className="text-sm text-bl-muted leading-relaxed">
                                You may contact me about my feedback.
                            </span>
                        </label>

                        {isVerifiedCandidate && (
                            <div>
                                <label className="block text-[10px] tracking-[0.18em] uppercase text-bl-muted font-medium mb-2">
                                    May we use this as a testimonial later?
                                </label>
                                <div className="space-y-1.5" data-testid="review-modal-permission">
                                    {PERMISSION_TYPES.map((p) => (
                                        <label
                                            key={p.id}
                                            className="flex items-start gap-2 cursor-pointer"
                                        >
                                            <input
                                                type="radio"
                                                name="permission"
                                                value={p.id}
                                                checked={permission === p.id}
                                                onChange={() => setPermission(p.id)}
                                                className="mt-1"
                                                data-testid={`review-modal-permission-${p.id}`}
                                            />
                                            <span className="text-sm text-bl leading-relaxed">
                                                {p.label}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                                <p className="mt-2 text-xs text-bl-muted leading-relaxed">
                                    Default is no permission. Nothing is published
                                    automatically — our team approves every
                                    testimonial manually.
                                </p>
                                {permission === "company" && (
                                    <input
                                        type="text"
                                        placeholder="Company name"
                                        value={companyName}
                                        onChange={(e) => setCompanyName(e.target.value)}
                                        className="w-full mt-2 px-3 py-2 rounded-md border border-bl bg-bl-surface text-bl text-sm"
                                        maxLength={160}
                                    />
                                )}
                            </div>
                        )}

                        <div className="flex gap-3 pt-2">
                            <button
                                onClick={onSubmit}
                                disabled={submitting}
                                className="bl-btn-primary flex-1"
                                data-testid="review-modal-submit-btn"
                            >
                                {submitting ? "Sending…" : "Send feedback"}
                            </button>
                            <button
                                onClick={onClose}
                                className="bl-btn-secondary"
                                data-testid="review-modal-cancel-btn"
                            >
                                <X className="w-4 h-4" /> Cancel
                            </button>
                        </div>
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
}
